import React, { useMemo } from "react";
import { logout, getUsername } from "../../services/auth";
import { useBackendStatus } from "../../hooks/useBackendStatus";
import ModuleShellHeader from "../common/ModuleShellHeader";
import type { ModuleTab } from "../common/ModuleShellHeader";
import { useI18n } from "../../i18n/I18nProvider";
// Keep legacy CSS for any overrides that may exist
import "./DoctorModuleHeader.css";

export type DoctorModuleTab =
    | "dashboard"
    | "clinical-notes"
    | "toothchart"
    | "treatment-plan"
    | "perio"
    | "history"
    | "prescription"
    | "review";

interface DoctorModuleHeaderProps {
    activeTab: DoctorModuleTab;
    onSelectTab: (_tab: DoctorModuleTab) => void;
    isSessionActive: boolean;
    patientName?: string;
    visitId?: string | number;
    onEndSession?: () => void;
}

export default function DoctorModuleHeader({
    activeTab,
    onSelectTab,
    isSessionActive,
}: DoctorModuleHeaderProps) {
    const { isOnline } = useBackendStatus();
    const { t } = useI18n();
    const username = getUsername() || t("doctor_role_default");

    const tabs = useMemo(
        (): ModuleTab<DoctorModuleTab>[] => [
            { key: "dashboard",      label: t("doctor_tab_dashboard"),     icon: "dashboard",    disabled: false },
            { key: "clinical-notes", label: t("doctor_tab_notes"),         icon: "notes",        disabled: !isSessionActive || !isOnline },
            { key: "toothchart",     label: t("doctor_tab_toothchart"),    icon: "tooth",        disabled: !isSessionActive || !isOnline },
            { key: "treatment-plan", label: t("doctor_tab_treatment_plan"),icon: "chart",        disabled: !isSessionActive || !isOnline },
            { key: "perio",          label: t("doctor_tab_perio"),         icon: "perio",        disabled: !isSessionActive || !isOnline },
            { key: "history",        label: t("doctor_tab_history"),       icon: "history",      disabled: !isSessionActive || !isOnline },
            { key: "prescription",   label: t("doctor_tab_prescriptions"), icon: "prescription", disabled: !isSessionActive || !isOnline },
            { key: "review",         label: t("doctor_tab_review_sign"),   icon: "review",       disabled: !isSessionActive || !isOnline },
        ],
        [isSessionActive, isOnline, t]
    );

    return (
        <ModuleShellHeader<DoctorModuleTab>
            theme="doctor"
            brandName={t("doctor_brand_name")}
            brandSub={t("doctor_brand_sub")}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onSelectTab}
            user={{ name: username, role: t("doctor_role_clinician") }}
            onLogout={() => { logout(); window.location.href = "/"; }}
            showDate
        />
    );
}
